import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import {
  activateInlineLandingCampaignWidget,
  loadLandingCampaignWidget,
  teardownLandingCampaignWidget,
} from '@/lib/landingCampaignWidget'
import type { LandingCampaign } from '@/types/landing'
import styles from './LandingPageInlineWidgetCheckout.module.css'

type LoadPhase = 'idle' | 'loading' | 'ready' | 'error'

interface LandingPageInlineWidgetCheckoutProps {
  campaign: LandingCampaign
  className?: string
  thankYouPaymentId?: number
  thankYouLuckyNumbers?: string[]
  thankYouCheckoutToken?: string
}

export function LandingPageInlineWidgetCheckout({
  campaign,
  className,
  thankYouPaymentId,
  thankYouLuckyNumbers,
  thankYouCheckoutToken,
}: LandingPageInlineWidgetCheckoutProps) {
  const embedHostRef = useRef<HTMLDivElement>(null)
  const [phase, setPhase] = useState<LoadPhase>('idle')
  const [reloadKey, setReloadKey] = useState(0)
  const campaignId = campaign.id

  const activateWidget = useCallback(
    async (isActive: () => boolean) => {
      const host = embedHostRef.current
      if (!host) {
        return
      }

      setPhase('loading')

      try {
        const instanceId = await loadLandingCampaignWidget(campaign, {
          thankYouPaymentId,
          thankYouLuckyNumbers,
          thankYouCheckoutToken,
        })
        if (!isActive()) {
          return
        }

        const relocated = await activateInlineLandingCampaignWidget(instanceId, host)
        if (!isActive()) {
          return
        }

        if (!relocated) {
          teardownLandingCampaignWidget(campaignId)
          setPhase('error')
          return
        }

        setPhase('ready')
      } catch {
        if (!isActive()) {
          return
        }
        teardownLandingCampaignWidget(campaignId)
        setPhase('error')
      }
    },
    [campaign, campaignId, thankYouPaymentId, thankYouLuckyNumbers, thankYouCheckoutToken]
  )

  useEffect(() => {
    let active = true
    const isActive = (): boolean => active

    void Promise.resolve().then(() => {
      if (active) return activateWidget(isActive)
    })

    return () => {
      active = false
      teardownLandingCampaignWidget(campaignId)
    }
  }, [activateWidget, campaignId, reloadKey])

  const shellClassName = className ? `${styles.shell} ${className}` : styles.shell
  const showLoadingStatus = phase === 'loading' || phase === 'idle'

  return (
    <section
      className={shellClassName}
      role="region"
      aria-label="Compra de números"
      aria-busy={showLoadingStatus}
    >
      {showLoadingStatus && (
        <div className={styles.status}>
          <Icon icon="mdi:loading" className={styles.statusIcon} aria-hidden />
          <p className={styles.statusText}>Carregando checkout...</p>
        </div>
      )}

      {phase === 'error' && (
        <div className={styles.status}>
          <Icon
            icon="mdi:alert-circle-outline"
            className={styles.statusIconError}
            aria-hidden
          />
          <p className={styles.statusText}>
            Não foi possível carregar o checkout agora. Tente novamente em instantes.
          </p>
          <button
            type="button"
            className={styles.retryButton}
            onClick={() => {
              teardownLandingCampaignWidget(campaignId)
              setReloadKey((current) => current + 1)
            }}
          >
            Tentar novamente
          </button>
        </div>
      )}

      <div
        ref={embedHostRef}
        className={styles.embedHost}
        hidden={phase === 'error'}
        aria-hidden={phase === 'error' || showLoadingStatus}
      />
    </section>
  )
}
